import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  ISSUE_SEVERITIES,
  ISSUE_SEVERITY_META,
  ISSUE_STATUSES,
  ISSUE_STATUS_META,
  collapseBreadcrumbs,
  describeBreadcrumb,
  formatTicketMarkdown,
  primeiraFalha,
  sourceHintForRoute,
  type Issue,
  type IssueSeverity,
  type IssueStatus,
} from "@brasilnovo/shared";
import { api } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { useImagemProtegida } from "../lib/useImagemProtegida.js";
import { Card, ErrorBox, Kpi, PageHeader, Spinner } from "../components/ui.js";

// =====================================================================
// Painel de chamados.
//
// Não é uma tabela de registros: é uma FILA DE TRABALHO. A tela responde,
// em ordem, "o que está me travando agora?", "de onde vem?" e "o que eu
// faço com isso?". Por isso a lista já chega ordenada por gravidade e o
// detalhe abre ao lado, sem trocar de página — triagem é uma sequência de
// decisões rápidas, e cada navegação perdida é uma delas abandonada.
// =====================================================================

interface ItemFila extends Issue {
  emAberto: boolean;
  comentarios: number;
  ultimoMovimentoAt: string | null;
  ultimoMovimentoPor: string | null;
}

interface Stats {
  abertos: number;
  bloqueando: number;
  emAndamento: number;
  resolvidos7d: number;
  horasAteResolver: number | null;
  diasMaisAntigoAberto: number | null;
  porRota: { route: string; total: number; abertos: number }[];
}

interface Evento {
  id: number;
  at: string;
  actorEmail: string | null;
  actorName: string | null;
  kind: string;
  fromStatus: string | null;
  toStatus: string | null;
  body: string;
}

const dataHora = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

/** Salva o ticket como arquivo, a partir do Markdown já montado no cliente. */
function baixarTicket(issue: Issue, markdown: string): void {
  const url = URL.createObjectURL(new Blob([markdown], { type: "text/markdown;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${issue.code}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

const relativo = (iso: string | null): string => {
  if (!iso) return "—";
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `há ${h} h`;
  return `há ${Math.round(h / 24)} d`;
};

const TOM_STATUS: Record<string, string> = {
  danger: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-700",
  info: "bg-blue-100 text-blue-700",
  success: "bg-green-100 text-green-700",
  neutral: "bg-slate-100 text-slate-600",
};

function BadgeStatus({ status }: { status: IssueStatus }) {
  const m = ISSUE_STATUS_META[status];
  return <span className={`badge ${TOM_STATUS[m.tone]}`}>{m.label}</span>;
}

function BadgeSeveridade({ severity }: { severity: IssueSeverity }) {
  const m = ISSUE_SEVERITY_META[severity];
  return (
    <span className={`badge ${TOM_STATUS[m.tone]}`} title={m.label}>
      {m.prioridade}
    </span>
  );
}

// ---------------------------------------------------------------------

export function Chamados() {
  const { user, can } = useAuth();
  const tria = can("chamados.triagem");
  const [params, setParams] = useSearchParams();
  const selecionado = params.get("id") ? Number(params.get("id")) : null;

  const [filtroAberto, setFiltroAberto] = useState<"todos" | "abertos" | "encerrados">("abertos");
  const [filtroSeveridade, setFiltroSeveridade] = useState<string>("");
  const [busca, setBusca] = useState("");

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (filtroAberto === "abertos") p.set("aberto", "1");
    if (filtroAberto === "encerrados") p.set("aberto", "0");
    if (filtroSeveridade) p.set("severity", filtroSeveridade);
    if (busca.trim()) p.set("q", busca.trim());
    return p.toString();
  }, [filtroAberto, filtroSeveridade, busca]);

  const fila = useQuery({
    queryKey: ["issues", query],
    queryFn: async () => (await api.get(`/issues?${query}`)).data as ItemFila[],
  });

  const stats = useQuery({
    queryKey: ["issues-stats"],
    queryFn: async () => (await api.get("/issues/stats")).data as Stats,
    enabled: tria,
  });

  const selecionar = (id: number | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set("id", String(id));
    else p.delete("id");
    setParams(p, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="Chamados"
        subtitle={
          tria
            ? "Fila de erros relatados pelo portal — ordenada por impacto."
            : "Os erros que você relatou e o que aconteceu com eles."
        }
      />

      {tria && stats.data && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Kpi
            label="Bloqueando alguém"
            value={stats.data.bloqueando}
            hint={stats.data.bloqueando ? "P1 em aberto — olhe hoje" : "nenhum P1 pendente"}
          />
          <Kpi label="Em aberto" value={stats.data.abertos} hint={`${stats.data.emAndamento} em andamento`} />
          <Kpi
            label="Fila mais antiga"
            value={stats.data.diasMaisAntigoAberto == null ? "—" : `${Math.floor(stats.data.diasMaisAntigoAberto)} d`}
            hint="idade do chamado aberto mais velho"
          />
          <Kpi
            label="Resolvidos (7 d)"
            value={stats.data.resolvidos7d}
            hint={
              stats.data.horasAteResolver == null
                ? "sem histórico"
                : `média de ${Math.round(stats.data.horasAteResolver)} h até resolver`
            }
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-brand-100 bg-white p-0.5">
          {(["abertos", "todos", "encerrados"] as const).map((v) => (
            <button
              key={v}
              data-testid={`filtro-${v}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium capitalize transition ${
                filtroAberto === v ? "bg-brand-600 text-white" : "text-slate-600 hover:bg-brand-50"
              }`}
              onClick={() => setFiltroAberto(v)}
            >
              {v}
            </button>
          ))}
        </div>
        <select className="input w-auto" value={filtroSeveridade} onChange={(e) => setFiltroSeveridade(e.target.value)}>
          <option value="">Todas as prioridades</option>
          {ISSUE_SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {ISSUE_SEVERITY_META[s].prioridade} — {ISSUE_SEVERITY_META[s].label}
            </option>
          ))}
        </select>
        <input
          className="input w-auto flex-1 min-w-[12rem]"
          placeholder="Buscar no título ou na descrição…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </div>

      {fila.isError && <ErrorBox error={fila.error} />}
      {fila.isLoading ? (
        <Spinner />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          <ListaFila itens={fila.data ?? []} selecionado={selecionado} onSelecionar={selecionar} />
          <div className="lg:sticky lg:top-6 lg:self-start">
            {selecionado ? (
              <Detalhe id={selecionado} podeTriar={tria} onFechar={() => selecionar(null)} />
            ) : (
              <Card className="flex min-h-[16rem] items-center justify-center text-center text-sm text-slate-400">
                Selecione um chamado para ver a captura, os passos e o ticket.
              </Card>
            )}
          </div>
        </div>
      )}

      {tria && stats.data && stats.data.porRota.length > 0 && (
        <Card className="mt-6">
          <h2 className="text-sm font-semibold text-slate-800">Telas que mais geram chamado</h2>
          {/* Onde investir não é uma opinião — é esta lista. */}
          <p className="mb-3 mt-0.5 text-xs text-slate-500">Concentração de relatos por rota do portal.</p>
          <div className="space-y-1.5">
            {stats.data.porRota.map((r) => {
              const maior = stats.data!.porRota[0].total || 1;
              return (
                <div key={r.route} className="flex items-center gap-3 text-xs">
                  <code className="w-40 shrink-0 truncate text-slate-600">{r.route}</code>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-brand-500" style={{ width: `${(r.total / maior) * 100}%` }} />
                  </div>
                  <span className="w-24 shrink-0 text-right text-slate-500">
                    {r.total} · {r.abertos} aberto{r.abertos === 1 ? "" : "s"}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------

function ListaFila({
  itens,
  selecionado,
  onSelecionar,
}: {
  itens: ItemFila[];
  selecionado: number | null;
  onSelecionar: (id: number) => void;
}) {
  if (!itens.length) {
    return (
      <Card className="flex min-h-[16rem] flex-col items-center justify-center gap-2 text-center">
        <span className="text-3xl">🎉</span>
        <p className="text-sm text-slate-500">Nenhum chamado com esses filtros.</p>
      </Card>
    );
  }
  return (
    <ul className="space-y-2" data-testid="lista-chamados">
      {itens.map((i) => (
        <li key={i.id}>
          <button
            data-testid={`chamado-${i.id}`}
            onClick={() => onSelecionar(i.id)}
            className={`w-full rounded-xl border bg-white p-4 text-left transition hover:border-brand-300 ${
              selecionado === i.id ? "border-brand-500 ring-2 ring-brand-100" : "border-brand-100"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <BadgeSeveridade severity={i.severity} />
                  <BadgeStatus status={i.status} />
                  <code className="text-[11px] font-medium text-slate-400">{i.code}</code>
                </div>
                <p className="mt-1.5 truncate text-sm font-medium text-slate-800">{i.title}</p>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {sourceHintForRoute(i.route)?.area ?? i.route} · {i.reporterName ?? "—"} · {relativo(i.createdAt)}
                </p>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1 text-[11px] text-slate-400">
                {i.hasScreenshot && <span title="Tem captura de tela">🖼</span>}
                {i.comentarios > 0 && <span title={`${i.comentarios} movimentos`}>💬 {i.comentarios}</span>}
              </div>
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------

function Detalhe({ id, podeTriar, onFechar }: { id: number; podeTriar: boolean; onFechar: () => void }) {
  const qc = useQueryClient();
  const { user, can } = useAuth();
  const podeExcluir = can("sistema");
  const [aba, setAba] = useState<"resumo" | "captura" | "passos" | "ticket">("resumo");
  const [notas, setNotas] = useState<string | null>(null);
  const [comentario, setComentario] = useState("");
  const [copiado, setCopiado] = useState(false);
  // Confirmação em DOIS toques, não um `confirm()` do navegador: o diálogo
  // nativo é fácil de despachar no reflexo, e isto apaga a captura junto.
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

  const issue = useQuery({
    queryKey: ["issue", id],
    queryFn: async () => (await api.get(`/issues/${id}`)).data as Issue,
  });
  const eventos = useQuery({
    queryKey: ["issue-events", id],
    queryFn: async () => (await api.get(`/issues/${id}/events`)).data as Evento[],
  });

  // A aba volta ao resumo a cada chamado: manter "ticket" aberto faria a
  // triagem do próximo abrir num texto em vez de na descrição do problema.
  useEffect(() => {
    setAba("resumo");
    setNotas(null);
    setComentario("");
    setConfirmandoExclusao(false);
  }, [id]);

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["issue", id] });
    qc.invalidateQueries({ queryKey: ["issue-events", id] });
    qc.invalidateQueries({ queryKey: ["issues"] });
    qc.invalidateQueries({ queryKey: ["issues-stats"] });
  };

  const alterar = useMutation({
    mutationFn: async (patch: Record<string, unknown>) => (await api.patch(`/issues/${id}`, patch)).data,
    onSuccess: invalidar,
  });
  const comentar = useMutation({
    mutationFn: async () => (await api.post(`/issues/${id}/comments`, { body: comentario })).data,
    onSuccess: () => {
      setComentario("");
      invalidar();
    },
  });
  const excluir = useMutation({
    mutationFn: async () => (await api.delete(`/issues/${id}`)).data,
    onSuccess: () => {
      invalidar();
      onFechar();
    },
  });

  if (issue.isLoading) return <Card><Spinner /></Card>;
  if (issue.isError) return <Card><ErrorBox error={issue.error} /></Card>;
  const i = issue.data!;

  const sev = ISSUE_SEVERITY_META[i.severity];
  const origem = sourceHintForRoute(i.route);
  const falha = primeiraFalha(i.breadcrumbs);
  const passos = collapseBreadcrumbs(i.breadcrumbs);
  const ticket = formatTicketMarkdown(i, window.location.origin);

  const copiarTicket = async () => {
    try {
      await navigator.clipboard.writeText(ticket);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      /* clipboard bloqueado: o texto continua visível na aba para seleção manual */
    }
  };

  return (
    <Card className="overflow-hidden p-0">
      <div className="border-b border-brand-100 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <code className="text-xs font-semibold text-brand-700" data-testid="detalhe-codigo">{i.code}</code>
              <BadgeSeveridade severity={i.severity} />
              <BadgeStatus status={i.status} />
            </div>
            <h2 className="mt-2 text-base font-semibold leading-snug text-slate-900">{i.title}</h2>
            <p className="mt-1 text-xs text-slate-500">
              {i.reporterName ?? "—"} ({i.reporterRole ?? "—"}) · {dataHora(i.createdAt)} · alvo {sev.sla}
            </p>
          </div>
          <button className="shrink-0 text-slate-400 hover:text-slate-600" onClick={onFechar} aria-label="Fechar">✕</button>
        </div>

        {podeTriar && (
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="acoes-status">
            {ISSUE_STATUSES.map((s) => (
              <button
                key={s}
                data-testid={`status-${s}`}
                disabled={alterar.isPending || i.status === s}
                onClick={() => alterar.mutate({ status: s })}
                className={`rounded-lg px-2.5 py-1 text-xs font-medium transition ${
                  i.status === s
                    ? "bg-brand-600 text-white"
                    : "border border-brand-100 bg-white text-slate-600 hover:bg-brand-50 disabled:opacity-50"
                }`}
                title={ISSUE_STATUS_META[s].descricao}
              >
                {ISSUE_STATUS_META[s].label}
              </button>
            ))}
          </div>
        )}

        {podeExcluir && (
          <div className="mt-3 flex items-center gap-2 text-xs">
            {confirmandoExclusao ? (
              <>
                <span className="text-red-600">
                  Excluir apaga o chamado, o histórico e a captura. Não dá para desfazer.
                </span>
                <button
                  className="rounded-lg bg-red-600 px-2.5 py-1 font-medium text-white hover:bg-red-700"
                  data-testid="confirmar-exclusao"
                  disabled={excluir.isPending}
                  onClick={() => excluir.mutate()}
                >
                  Excluir
                </button>
                <button className="text-slate-500 hover:underline" onClick={() => setConfirmandoExclusao(false)}>
                  Cancelar
                </button>
              </>
            ) : (
              <button
                className="text-slate-400 hover:text-red-600 hover:underline"
                data-testid="excluir-chamado"
                onClick={() => setConfirmandoExclusao(true)}
              >
                Excluir chamado
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex border-b border-brand-100 text-sm">
        {([
          ["resumo", "Resumo"],
          ["captura", i.hasScreenshot ? "Captura" : "Captura (—)"],
          ["passos", `Passos (${passos.length})`],
          ["ticket", "Ticket"],
        ] as const).map(([k, rotulo]) => (
          <button
            key={k}
            data-testid={`aba-${k}`}
            onClick={() => setAba(k)}
            className={`flex-1 px-3 py-2.5 font-medium transition ${
              aba === k ? "border-b-2 border-brand-600 text-brand-700" : "text-slate-500 hover:bg-brand-50/60"
            }`}
          >
            {rotulo}
          </button>
        ))}
      </div>

      <div className="max-h-[60vh] overflow-y-auto p-5">
        {aba === "resumo" && (
          <div className="space-y-4 text-sm">
            <Bloco titulo="O que aconteceu">
              <p className="whitespace-pre-wrap text-slate-700">{i.description || "—"}</p>
            </Bloco>
            {i.expected && (
              <Bloco titulo="O que era para acontecer">
                <p className="whitespace-pre-wrap text-slate-700">{i.expected}</p>
              </Bloco>
            )}
            {falha && (
              <Bloco titulo="Ponto da falha">
                <code className="block rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
                  {describeBreadcrumb(falha)}
                </code>
              </Bloco>
            )}
            {origem && (
              <Bloco titulo="Por onde começar">
                <p className="mb-1 text-xs text-slate-500">Área {origem.area}</p>
                <ul className="space-y-0.5">
                  {origem.files.map((f) => (
                    <li key={f}><code className="text-xs text-slate-600">{f}</code></li>
                  ))}
                </ul>
              </Bloco>
            )}
            <Bloco titulo="Ambiente">
              <dl className="grid grid-cols-[7rem_1fr] gap-x-3 gap-y-1 text-xs text-slate-600">
                <dt className="text-slate-400">Tela</dt><dd><code>{i.route}</code></dd>
                <dt className="text-slate-400">Versão</dt><dd>{i.client.appVersion}</dd>
                <dt className="text-slate-400">Janela</dt><dd>{i.client.viewport} (tela {i.client.screen})</dd>
                <dt className="text-slate-400">Fuso</dt><dd>{i.client.timezone || "—"}</dd>
                <dt className="text-slate-400">Navegador</dt><dd className="break-all">{i.client.userAgent || "—"}</dd>
              </dl>
            </Bloco>

            <LinhaDoTempo eventos={eventos.data ?? []} />

            {podeTriar && (
              <Bloco titulo="Notas do time">
                <textarea
                  className="input min-h-[4rem] text-xs"
                  data-testid="detalhe-notas"
                  placeholder="Causa provável, decisão de triagem, onde foi corrigido…"
                  value={notas ?? i.devNotes}
                  onChange={(e) => setNotas(e.target.value)}
                />
                <button
                  className="btn-ghost mt-2 text-xs"
                  disabled={notas === null || notas === i.devNotes || alterar.isPending}
                  onClick={() => alterar.mutate({ devNotes: notas })}
                >
                  Salvar nota
                </button>
              </Bloco>
            )}

            <Bloco titulo="Responder a quem relatou">
              <textarea
                className="input min-h-[3rem] text-xs"
                data-testid="detalhe-comentario"
                placeholder="Uma resposta curta fecha o ciclo — quem relatou fica sabendo que foi lido."
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
              />
              <button
                className="btn-primary mt-2 text-xs"
                disabled={!comentario.trim() || comentar.isPending}
                onClick={() => comentar.mutate()}
              >
                Comentar
              </button>
            </Bloco>
          </div>
        )}

        {aba === "captura" && <AbaCaptura issue={i} />}

        {aba === "passos" && (
          <ol className="space-y-1.5 text-xs" data-testid="detalhe-passos">
            {passos.length === 0 && <li className="text-slate-400">Nenhum passo capturado.</li>}
            {passos.map((b, n) => (
              <li key={n} className={`flex gap-2 ${b.ok === false ? "font-medium text-red-600" : "text-slate-600"}`}>
                <span className="w-5 shrink-0 text-right text-slate-300">{n + 1}</span>
                <span className="break-words">{describeBreadcrumb(b)}</span>
              </li>
            ))}
          </ol>
        )}

        {aba === "ticket" && (
          <div>
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-xs text-slate-500">Pronto para colar em GitHub, Jira ou Slack.</p>
              <div className="flex gap-2">
                {/* Baixa a partir de um Blob local, não da rota da API: um
                    <a href="/api/v1/..."> é navegação do NAVEGADOR e não
                    leva o token — abriria um 401 em vez do arquivo. E o
                    Markdown já foi montado aqui, então a ida ao servidor
                    não traria nada de novo. */}
                <button className="btn-ghost text-xs" data-testid="baixar-ticket" onClick={() => baixarTicket(i, ticket)}>
                  Baixar .md
                </button>
                <button className="btn-primary text-xs" data-testid="copiar-ticket" onClick={() => void copiarTicket()}>
                  {copiado ? "Copiado ✓" : "Copiar ticket"}
                </button>
              </div>
            </div>
            {/* `whitespace-pre-wrap` e não rolagem horizontal: linhas de
                ticket são frases, e uma frase cortada na borda direita
                obriga a rolar para descobrir o que o defeito era. */}
            <pre
              className="max-h-[45vh] overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-slate-900 p-4 text-[11px] leading-relaxed text-slate-100"
              data-testid="detalhe-ticket"
            >
              {ticket}
            </pre>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Aba da captura de tela.
 *
 * A imagem não pode vir de um `<img src="/api/v1/...">` direto: essa
 * requisição é do NAVEGADOR e não carrega o token do portal — resultado,
 * 401 e imagem quebrada. Ver useImagemProtegida.
 */
function AbaCaptura({ issue }: { issue: Issue }) {
  const { src, carregando, erro } = useImagemProtegida(
    issue.hasScreenshot ? `/issues/${issue.id}/screenshot` : null,
  );

  if (!issue.hasScreenshot) {
    return <p className="py-8 text-center text-sm text-slate-400">Este chamado foi enviado sem captura de tela.</p>;
  }
  if (carregando) return <Spinner label="Carregando a captura…" />;
  if (erro) return <ErrorBox error={erro} />;
  if (!src) return null;

  return (
    <a href={src} target="_blank" rel="noreferrer" title="Abrir em tamanho real">
      <img
        src={src}
        alt={`Captura de tela do chamado ${issue.code}`}
        data-testid="detalhe-captura"
        className="w-full rounded-lg border border-slate-200"
      />
    </a>
  );
}

function Bloco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{titulo}</h3>
      {children}
    </section>
  );
}

function LinhaDoTempo({ eventos }: { eventos: Evento[] }) {
  if (!eventos.length) return null;
  const ICONE: Record<string, string> = {
    criado: "🐞", status: "↻", nota: "✎", comentario: "💬", atribuicao: "👤",
  };
  return (
    <Bloco titulo="Linha do tempo">
      <ol className="space-y-2 border-l border-slate-200 pl-4">
        {eventos.map((e) => (
          <li key={e.id} className="relative text-xs">
            <span className="absolute -left-[1.32rem] top-0.5 text-[10px]">{ICONE[e.kind] ?? "•"}</span>
            <p className="text-slate-700">{e.body || e.kind}</p>
            <p className="text-slate-400">
              {e.actorName ?? e.actorEmail ?? "sistema"} · {dataHora(e.at)}
            </p>
          </li>
        ))}
      </ol>
    </Bloco>
  );
}
